import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';

const ICONS = {
    info: (color) => (
        <svg className="w-6 h-6 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    ),
    warning: (color) => (
        <svg className="w-6 h-6 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
    ),
    shield: (color) => (
        <svg className="w-6 h-6 shrink-0" style={{ color }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
    ),
};

const ICON_COLORS = {
    info: '#3b82f6',
    warning: '#f59e0b',
    shield: '#22c55e',
};

const BG_STYLES = {
    warning: (dark) => ({
        backgroundColor: dark ? 'rgba(245, 158, 11, 0.08)' : '#fffbeb',
        borderLeft: '4px solid #f59e0b',
    }),
    neutral: (dark) => ({
        backgroundColor: dark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
        borderLeft: `4px solid ${dark ? 'rgba(255,255,255,0.2)' : '#d1d5db'}`,
    }),
    transparent: (dark, accent) => ({
        backgroundColor: 'transparent',
        borderLeft: `4px solid ${accent}`,
    }),
};

export default function AffiliateDisclosureSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'expanded';
    const bgStyle = config.bg_style || 'neutral';
    const iconType = content.icon_type || 'info';

    const [isOpen, setIsOpen] = useState(false);

    const title = content.title || 'Aviso Legal';
    const text = content.text || '';
    const paragraphs = text.split('\n').filter(p => p.trim());

    const iconColor = ICON_COLORS[iconType] || ICON_COLORS.info;
    const iconRender = (ICONS[iconType] || ICONS.info)(iconColor);
    const bgFn = BG_STYLES[bgStyle] || BG_STYLES.neutral;
    const containerStyle = bgFn(s.dark, s.accent);

    const sectionBgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Collapsible variant ──
    if (variant === 'collapsible') {
        return (
            <section className={`py-4 relative ${s.radiusClass} ${s.shadowClass}`} style={sectionBgS}>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="rounded-lg p-4 transition-all duration-300" style={containerStyle}>
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="w-full flex items-center gap-3 text-left">
                            {iconRender}
                            <span className="text-sm font-bold flex-1" style={{ color: s.text }}>{title}</span>
                            <svg className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                                style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[1000px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                            <div className="space-y-2 pl-9">
                                {paragraphs.map((p, i) => (
                                    <p key={i} className="text-xs leading-relaxed" style={{ color: s.textSub }}>{p}</p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // ── Expanded variant (default) ──
    return (
        <section className={`py-4 relative ${s.radiusClass} ${s.shadowClass}`} style={sectionBgS}>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className="rounded-lg p-4" style={containerStyle}>
                    <div className="flex items-start gap-3">
                        {iconRender}
                        <div>
                            <p className="text-sm font-bold mb-2" style={{ color: s.text }}>{title}</p>
                            <div className="space-y-2">
                                {paragraphs.map((p, i) => (
                                    <p key={i} className="text-xs leading-relaxed" style={{ color: s.textSub }}>{p}</p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
